import Typography from '@mui/material/Typography';
import type { CSSProperties, FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';

type EdrTruncatedTextProps = {
  value: string;
  style?: CSSProperties;
  maxWidth?: number | string;
};

/** Single-line table cell text; tooltip only when the value is actually truncated. */
export const EdrTruncatedText: FC<EdrTruncatedTextProps> = ({
  value,
  style,
  maxWidth = '100%',
}) => {
  const text = value?.trim() || '—';
  const textRef = useRef<HTMLElement | null>(null);
  const [truncated, setTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = textRef.current;
    if (!el) {
      setTruncated(false);
      return;
    }
    setTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    measure();
  }, [text, maxWidth, measure]);

  useEffect(() => {
    const el = textRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  const content = (
    <Typography
      ref={textRef}
      noWrap
      style={{ fontSize: 12, maxWidth, ...style }}
    >
      {text}
    </Typography>
  );

  if (!truncated || text === '—') {
    return content;
  }

  return (
    <Tooltip title={text} placement="top" enterDelay={300}>
      {content}
    </Tooltip>
  );
};
